import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import Modal from '@/components/ui/Modal';

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDate: (date: Date) => void;
  selectedDate: string | null;
}

const formatDateToYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const CalendarModal: React.FC<CalendarModalProps> = ({
  isOpen,
  onClose,
  onSelectDate,
  selectedDate,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = (date: Date) => {
    const month = date.getMonth();
    const year = date.getFullYear();
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    const todayStr = formatDateToYMD(new Date());

    const monthNames = [
      'Januari',
      'Februari',
      'Maret',
      'April',
      'Mei',
      'Juni',
      'Juli',
      'Agustus',
      'September',
      'Oktober',
      'November',
      'Desember',
    ];

    const days = [];
    const dayNames = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-9 w-9"></div>);
    }

    // Days cells
    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(year, month, day);
      const formatted = formatDateToYMD(dateObj);
      const isPast = formatted < todayStr;
      const isSelected = selectedDate === formatted;

      days.push(
        <button
          key={day}
          disabled={isPast}
          onClick={() => onSelectDate(dateObj)}
          className={`h-9 w-9 flex items-center justify-center rounded-full text-xs font-bold transition-all cursor-pointer ${
            isPast
              ? 'text-gray-300 cursor-not-allowed'
              : isSelected
              ? 'bg-[#FC480D] text-white shadow-sm'
              : 'text-gray-700 hover:bg-orange-50 hover:text-[#FC480D]'
          }`}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="flex-1 min-w-[260px]">
        <div className="text-center font-extrabold text-sm mb-3 text-[#0E1726]">
          {monthNames[month]} {year}
        </div>
        <div className="grid grid-cols-7 gap-1 text-center mb-2">
          {dayNames.map((d) => (
            <div key={d} className="text-[10px] uppercase text-gray-400 font-extrabold tracking-wider">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 place-items-center">{days}</div>
      </div>
    );
  };

  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pilih Tanggal Booking" size="xl">
      <div className="p-2 sm:p-4 space-y-4">
        {/* Navigation */}
        <div className="flex justify-between items-center px-1">
          <button
            onClick={() =>
              setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
            }
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={
              currentMonth.getMonth() === new Date().getMonth() &&
              currentMonth.getFullYear() === new Date().getFullYear()
            }
          >
            <Icon icon="mdi:chevron-left" className="w-5 h-5 text-gray-700" />
          </button>
          <div className="font-extrabold text-sm text-[#0E1726]">Pilih Tanggal Sesuai Keinginan</div>
          <button
            onClick={() =>
              setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
            }
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon icon="mdi:chevron-right" className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* 2 Months View */}
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {renderCalendar(currentMonth)}
          <div className="hidden sm:block self-stretch w-px bg-gray-100"></div>
          {renderCalendar(nextMonth)}
        </div>

        <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
          <div className="text-xs text-gray-500 font-semibold flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FC480D]"></span>
            <span>Dipilih</span>
            <span className="w-2.5 h-2.5 rounded-full bg-gray-200 ml-2"></span>
            <span>Tersedia</span>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-100 text-gray-700 text-xs font-extrabold rounded-xl hover:bg-gray-200 transition-colors"
          >
            Tutup
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CalendarModal;
